import * as _ from 'underscore';

import { CronExpressionFieldSubsetTypes, CronExpressionHelper } from '../helpers/CronExpressionHelper';

import { Formatters, Messages } from '../../codegen/Localize';
import { L10n } from '../L10n/L10n';
import { Module } from '../Module';
import { ListFilter } from './ListFilter';

// Values adhere to Quartz extended format.
// http://www.quartz-scheduler.org/documentation/quartz-2.x/tutorials/crontrigger
// http://www.cronmaker.com/

const weekdays = [
  Messages.dayNameSun,
  Messages.dayNameMon,
  Messages.dayNameTue,
  Messages.dayNameWed,
  Messages.dayNameThu,
  Messages.dayNameFri,
  Messages.dayNameSat,
];

export interface IStringStringFilter {
  (value: string): string;
}

export interface INumberStringFilter {
  (value: number): string;
}

const addSentenceSeparator = (sentence: string): string => {
  if (sentence !== '') {
    return sentence + ' ';
  } else {
    return sentence;
  }
};

// return schedule description that cronExpression represents
function cronExpression(cronExpression: string) {
  const dayOfWeekField = CronExpressionHelper.getDayOfWeekField(cronExpression);

  // monthly schedule
  const type = CronExpressionHelper.getTypeFromFieldSubset(dayOfWeekField);
  if (type === CronExpressionFieldSubsetTypes.NthWeekday ||
    type === CronExpressionFieldSubsetTypes.LastWeekday) {
    return cronExpression_dayOfMonthFilter(cronExpression);
  }
  const dayOfMonth = cronExpression_dayOfMonthFilter(cronExpression);
  if (dayOfMonth !== '') {
    return dayOfMonth;
  }

  // hourly, daily or weekly schedule
  const dayOfWeek = cronExpression_dayOfWeekFieldFilter(dayOfWeekField);

  return cronExpression_minutesOfHourFieldFilter(CronExpressionHelper.getMinutesField(cronExpression)) +
    cronExpression_hoursOfDayFieldFilter(CronExpressionHelper.getHoursField(cronExpression)) +
    (dayOfWeek ?  ' ' + dayOfWeek : '');
}

export const CronExpressionFilter: IStringStringFilter = cronExpression;

Module.instance().filter('cronExpression', () => CronExpressionFilter);

function cronExpression_minutesOfHourFieldFilter(minutesOfHourField: string) {
  const minutesParts = CronExpressionHelper.getTimeFieldSubsetParts(minutesOfHourField);

  if (_.isNumber(minutesParts.increment)) {
    return sentenceAsFragmentFilter(
      Messages.remoteRefreshScheduleExpressionStatementFragmentMinuteIncrement({
        MINUTE_COUNT: minutesParts.increment,
        MINUTE_COUNT_LOC: Formatters.formatCount(minutesParts.increment)
      })
    );
  } else {
    return '';
  }
}

export const CronExpression_MinutesOfHourFieldFilter: IStringStringFilter = cronExpression_minutesOfHourFieldFilter;
Module.instance().filter('cronExpression_minutesOfHourField', () => CronExpression_MinutesOfHourFieldFilter);

function cronExpression_hoursOfDayFieldFilter(hourOfDayField: string) {
  let translation = '';
  let fragment = '';

  const hoursParts = CronExpressionHelper.getTimeFieldSubsetParts(hourOfDayField);

  if (_.isNumber(hoursParts.increment)) {
    fragment = Messages.remoteRefreshScheduleExpressionStatementFragmentHourIncrement({
      HOUR_COUNT: hoursParts.increment,
      HOUR_COUNT_LOC: Formatters.formatCount(hoursParts.increment)
    });
    translation = sentenceAsFragmentFilter(fragment);
  }

  if (_.isNumber(hoursParts.from) && _.isNumber(hoursParts.to)) {
    // fix the date to occur on a day with no DST implications
    const hoursFromDate = new Date(2015, 5, 1, hoursParts.from);
    const hoursToDate = new Date(2015, 5, 1, hoursParts.to);
    fragment = Messages.remoteRefreshScheduleExpressionStatementFragmentRange({
      FROM_DATETIME: L10n.localizedShortTime(hoursFromDate),
      TO_DATETIME: L10n.localizedShortTime(hoursToDate)
    });
    translation = translation + ' ' + sentenceAsFragmentFilter(fragment);
  }

  if (_.isNumber(hoursParts.specific)) {
    // fix the date to occur on a day with no DST implications
    const hoursSpecificDate = new Date(2015, 5, 1, hoursParts.specific);

    fragment = Messages.remoteRefreshScheduleExpressionStatementFragmentSpecific({
      DATETIME: L10n.localizedShortTime(hoursSpecificDate)
    });
    translation = sentenceAsFragmentFilter(fragment);
  }

  return translation;
}

export const CronExpression_HoursOfDayFieldFilter: IStringStringFilter = cronExpression_hoursOfDayFieldFilter;
Module.instance().filter('cronExpression_hoursOfDayField', () => CronExpression_HoursOfDayFieldFilter);

function cronExpression_dayOfWeekFieldFilter(dayOfWeekField: string) {
  let translation = '';
  let fragment = '';

  _.each(dayOfWeekField.split(','), (subset: string) => {
    const type = CronExpressionHelper.getTypeFromFieldSubset(subset);

    if (type === CronExpressionFieldSubsetTypes.Range) {
      translation = addSentenceSeparator(translation);

      const dash = subset.indexOf('-');
      const start = subset.substring(0, dash);
      const end = subset.substring(dash + 1);

      fragment = Messages.remoteRefreshScheduleExpressionStatementFragmentRange({
        FROM_DATETIME: cronExpression_specificDayOfWeekFilter(start),
        TO_DATETIME: cronExpression_specificDayOfWeekFilter(end)
      });
      translation = translation + sentenceAsFragmentFilter(fragment);

    } else if (type === CronExpressionFieldSubsetTypes.Specific) {
      translation = addSentenceSeparator(translation);

      fragment = cronExpression_specificDayOfWeekFilter(subset[0]);
      translation = translation + sentenceAsFragmentFilter(fragment);

    } else if (type === CronExpressionFieldSubsetTypes.All ||
      type === CronExpressionFieldSubsetTypes.Any) {
      // Imply the full week by not specifying anything.
    } else {
      throw 'Cron day of week expression could not be parsed: ' + dayOfWeekField;
    }
  });

  return translation;
}

export const CronExpression_DayOfWeekFieldFilter: IStringStringFilter = cronExpression_dayOfWeekFieldFilter;
Module.instance().filter('cronExpression_dayOfWeekField', () => CronExpression_DayOfWeekFieldFilter);

function cronExpression_specificDayOfWeekFilter(dayOfWeek: string) {
  // This array has Sunday at index 0 or 6 depending on the locale.
  const wideDayNames = L10n.wideDayNames();
  // returns 0 for Sunday, 1 for Monday
  const firstDayOfWeek = L10n.firstDayOfWeek();

  return wideDayNames[(parseInt(dayOfWeek, 10) + 6 - firstDayOfWeek) % 7];
}

export const CronExpression_SpecificDayOfWeekFilter: IStringStringFilter = cronExpression_specificDayOfWeekFilter;
Module.instance().filter('cronExpression_specificDayOfWeek', () => CronExpression_SpecificDayOfWeekFilter);

function cronExpression_dayOfMonthFilter(expression: string) {
  let fragment = '';
  const dayOfWeekField = CronExpressionHelper.getDayOfWeekField(expression);
  const dayOfMonth = CronExpressionHelper.getDayOfMonthField(expression);
  const hourOfDay = CronExpressionHelper.getHoursField(expression);

  // monthly schedule comes from dayOfWeekField such as '4L' or '5#1'
  const dayOfWeekFieldType = CronExpressionHelper.getTypeFromFieldSubset(dayOfWeekField);
  if (dayOfWeekFieldType === CronExpressionFieldSubsetTypes.NthWeekday ||
    dayOfWeekFieldType === CronExpressionFieldSubsetTypes.LastWeekday) {
    // fix the date to occur on a day with no DST implications
    const hoursSpecificDate = new Date(2015, 5, 1, parseInt(hourOfDay, 10));
    const weekday = CronExpressionHelper.getWeekdayPartFromSubset(dayOfWeekField);
    if (dayOfWeekFieldType === CronExpressionFieldSubsetTypes.LastWeekday) {
      fragment = Messages.scheduleMonthlyDescriptionLastWeekday({
        START: L10n.localizedShortTime(hoursSpecificDate),
        WEEKDAY: weekdays[weekday - 1]()
      });
    } else {
      const day = CronExpressionHelper.getNthPartFromSubset(dayOfWeekField);
      let weekdayIndex = Messages.dayOfMonth({
        DAY: day,
        DAY_LOC: Formatters.formatCount(day)
      });
      fragment = Messages.scheduleMonthlyDescriptionWeekday({
        START: L10n.localizedShortTime(hoursSpecificDate),
        NTH: weekdayIndex,
        WEEKDAY: weekdays[weekday - 1]()
      });
    }
    return sentenceAsFragmentFilter(fragment);
  }

  // monthly schedule comes from dayOfMonth field such ash 'L' or '5,6'
  if (dayOfMonth.search('[0-9L]') !== -1) {
    // fix the date to occur on a day with no DST implications
    const hoursSpecificDate = new Date(2015, 5, 1, parseInt(hourOfDay, 10));
    if (dayOfMonth === 'L') {
      fragment = Messages.scheduleMonthlyDescriptionLast({START: L10n.localizedShortTime(hoursSpecificDate)});
    } else {
      const daysOfMonth: string[] = [];
      _.each(dayOfMonth.split(','), (subset: string) => {
        const day = parseInt(subset, 10);
        daysOfMonth.push(Messages.dayOfMonth({
          DAY: day,
          DAY_LOC: Formatters.formatCount(day),
        }));
      });
      fragment = Messages.scheduleMonthlyDescription({
        START: L10n.localizedShortTime(hoursSpecificDate),
        DAYOFMONTH: ListFilter(daysOfMonth)
      });
    }

    return sentenceAsFragmentFilter(fragment);
  }

  // return empty string indicating it is not monthly schedule
  return '';
}

export const CronExpression_DayOfMonthFilter: IStringStringFilter = cronExpression_dayOfMonthFilter;
Module.instance().filter('cronExpression_dayOfMonth', () => CronExpression_DayOfMonthFilter);

function sentenceAsFragmentFilter(fragment: string) {
  return Messages.remoteRefreshScheduleExpressionStatementSentence({ FRAGMENT: fragment });
}

export const SentenceAsFragmentFilter: IStringStringFilter = sentenceAsFragmentFilter;
Module.instance().filter('sentenceAsFragment', () => SentenceAsFragmentFilter);

function minutesOrHoursFilter(minutes: number) {
  if (minutes < 60) {
    return Messages.minutes({
      MINUTES: minutes,
      MINUTES_LOC: Formatters.formatCount(minutes)
    });
  } else {
    const hours = Math.floor(minutes / 60);
    return Messages.hours({
      HOURS: hours,
      HOURS_LOC: Formatters.formatCount(hours)
    });
  }
}

export const MinutesOrHoursFilter: INumberStringFilter = minutesOrHoursFilter;
Module.instance().filter('minutesOrHours', () => MinutesOrHoursFilter);
